/**
 * adminConfigurationModel — the one administration-state vocabulary for the product's
 * provider / model / Skill / tool / connection surfaces (FULL-0159 / PROD-UX-073,
 * MASTER_FLOW §14C.5, §3 and §5).
 *
 * The surfaces already existed but each one worded a state of its own. This module keeps the
 * four administration states the task requires distinct, and classifies them from the canonical
 * server projections only:
 *
 *   disabled              — switched off explicitly (a recorded off switch, or a lifecycle end)
 *   unavailable           — present, but a dependency/evidence means it cannot operate now
 *   pending_review        — a candidate waiting for a human review; review is not activation
 *   approval_required     — the next step is an approval, not an availability problem
 *   requires_configuration— nothing is configured yet; this is not "off" and not "broken"
 *   enabled               — explicitly on/available in the projection that owns the switch
 *   no_permission         — the caller may not read that evidence; never rendered as "off"
 *   unknown               — absent/unreadable; never guessed into a reassuring state
 *
 * AUTHORITY. Every classifier is a pure read over a server payload. Nothing here writes,
 * approves, grants, lowers a risk, resolves a credential or computes a transition. A state word
 * is a presentation of what the server reported, never a policy decision. The page that consumes
 * this module mounts no mutating endpoint; the existing audited surfaces remain the only writers.
 *
 * The labels borrow the canonical `statusVocabulary` tone classes so this is not a second palette.
 * `enabled` deliberately reads "מופעל" (recorded on) rather than "פעיל" (operating): a provider
 * record can be enabled while `live_routing_consumes_record` is false, and the consuming surface
 * must still render that caveat.
 */
import { LIVE_STATUS_CLS } from "./statusVocabulary";

const freezeResult = (state, reason) => Object.freeze({ state, reason });

const isObject = (value) => value != null && typeof value === "object" && !Array.isArray(value);

const asText = (value, max = 160) => {
  if (typeof value !== "string") return null;
  const text = value.trim();
  return text ? text.slice(0, max) : null;
};

const asBool = (value) => (value === true ? true : value === false ? false : null);

// ─── State vocabulary ────────────────────────────────────────────────────────

export const ADMIN_STATES = Object.freeze({
  enabled: Object.freeze({
    key: "enabled",
    label: "מופעל",
    meaning: "הגורם סומן כפעיל בפרויקציה שהבעלים של המצב; אין בכך כדי להעיד על ניתוב או ביצוע.",
    cls: LIVE_STATUS_CLS.active,
  }),
  disabled: Object.freeze({
    key: "disabled",
    label: "מושבת",
    meaning: "כובה במפורש או הוצא מכלל שימוש. זו אינה תקלה ואינה המתנה לאישור.",
    cls: LIVE_STATUS_CLS.disabled,
  }),
  unavailable: Object.freeze({
    key: "unavailable",
    label: "לא זמין",
    meaning: "הגורם קיים אך תלות או ראיות חסרות כדי שיפעל כעת. אין זו הגדרה חסרה.",
    cls: LIVE_STATUS_CLS.not_connected,
  }),
  pending_review: Object.freeze({
    key: "pending_review",
    label: "ממתין לסקירה",
    meaning: "מועמד שממתין לסקירה אנושית. סקירה אינה הפעלה ואינה מעניקה הרשאה.",
    cls: LIVE_STATUS_CLS.waiting_human,
  }),
  approval_required: Object.freeze({
    key: "approval_required",
    label: "דורש אישור",
    meaning: "השלב הבא מותנה באישור אנושי; הבקשה אינה הרשאה לבצע.",
    cls: LIVE_STATUS_CLS.approval_required,
  }),
  requires_configuration: Object.freeze({
    key: "requires_configuration",
    label: "דורש הגדרה",
    meaning: "טרם הוגדר דבר. אין זו השבתה ואין זו תקלה.",
    cls: LIVE_STATUS_CLS.requires_configuration,
  }),
  no_permission: Object.freeze({
    key: "no_permission",
    label: "אין הרשאה",
    meaning: "הקורא אינו מורשה לקרוא את הראיה. ההרשאה נקבעת בשרת, לא במשטח.",
    cls: LIVE_STATUS_CLS.no_permission,
  }),
  unknown: Object.freeze({
    key: "unknown",
    label: "לא ידוע",
    meaning: "המקור לא נטען או אינו קריא — אין הצגת מצב מומצא.",
    cls: LIVE_STATUS_CLS.unknown,
  }),
});

export const ADMIN_STATE_KEYS = Object.freeze(Object.keys(ADMIN_STATES));

/** The four states this round requires to stay distinguishable from one another. */
export const ADMIN_REQUIRED_DISTINCTIONS = Object.freeze([
  "disabled",
  "unavailable",
  "pending_review",
  "approval_required",
]);

/** Fail closed: an unrecognised key is `unknown`, never a more specific/reassuring state. */
export function adminStateMeta(key) {
  return typeof key === "string" && Object.prototype.hasOwnProperty.call(ADMIN_STATES, key)
    ? ADMIN_STATES[key]
    : ADMIN_STATES.unknown;
}

/**
 * Count states without collapsing them. Every administration state is present in the result
 * (including zeroes), so a consumer cannot mistake the absence of a key for a zero count.
 */
export function summarizeAdminStates(entries = []) {
  const counts = Object.fromEntries(ADMIN_STATE_KEYS.map((key) => [key, 0]));
  for (const entry of entries) {
    const key = typeof entry === "string" ? entry : entry?.state;
    counts[adminStateMeta(key).key] += 1;
  }
  return counts;
}

// ─── Providers and models ────────────────────────────────────────────────────

/**
 * A provisioned provider record, classified from its own `enabled` switch.
 *
 * `enabled: false` is NOT "disabled": the record exists and the two-person activation step is
 * outstanding, so the truthful state is `approval_required`. `enabled: true` is the recorded
 * operator activation; it does not by itself mean live traffic consumes the record — the caller
 * must render `providerRecordEvidence().liveRoutingConsumesRecord`.
 */
export function classifyProviderRecord(record) {
  if (!isObject(record) || !asText(record.provider_id)) {
    return freezeResult("unknown", "provider_record_unreadable");
  }
  if (record.enabled === true) return freezeResult("enabled", "enabled_by_operator_activation");
  if (record.enabled === false) {
    return freezeResult("approval_required", "activation_requires_two_person_approval");
  }
  return freezeResult("unknown", "enabled_not_reported");
}

/** The safe, machine-readable evidence a provider record carries. Never a credential value. */
export function providerRecordEvidence(record) {
  const source = isObject(record) ? record : {};
  const activation = isObject(source.activation) ? source.activation : {};
  const credential = isObject(source.credential) ? source.credential : {};
  return Object.freeze({
    activationState: asText(activation.state),
    credentialState: asText(credential.state),
    credentialScheme: asText(credential.scheme),
    credentialReferenceLength:
      typeof credential.reference_name_length === "number" &&
      Number.isInteger(credential.reference_name_length)
        ? credential.reference_name_length
        : null,
    credentialReferenceDigest: asText(credential.reference_name_digest, 200),
    credentialValueReturned: asBool(credential.value_returned),
    secretsProvisioned: asBool(activation.secrets_provisioned),
    connectionVerified: asBool(activation.connection_verified),
    routeChanged: asBool(activation.route_changed),
    authorityGranted: asBool(activation.authority_granted),
    auditWritten: asBool(activation.audit_written),
    liveRoutingConsumesRecord: asBool(source.live_routing_consumes_record),
  });
}

/**
 * The configured analysis provider/model. A missing provider or model is an unfinished
 * configuration; a provider/model that is present but has no usable provider key is
 * `unavailable` — the two are different operator actions in §5.
 */
export function classifyAiConfiguration(ai) {
  if (!isObject(ai)) return freezeResult("unknown", "ai_configuration_not_reported");
  if (ai.configured === true) return freezeResult("enabled", "configured_with_usable_provider_key");
  if (ai.configured === false) {
    if (!asText(ai.provider) || !asText(ai.model)) {
      return freezeResult("requires_configuration", "provider_or_model_not_configured");
    }
    return freezeResult("unavailable", "provider_key_not_usable");
  }
  return freezeResult("unknown", "configured_not_reported");
}

const AI_KEY_SOURCE_LABELS = Object.freeze({
  raw: "מפתח גולמי בהגדרה",
  reference: "הפניה לסוד",
  invalid_reference: "הפניה לסוד אינה תקינה",
  none: "לא הוגדר מפתח",
  unknown: "מקור המפתח לא דווח",
});

export function aiKeySourceLabel(source) {
  const key = asText(source, 40) || "unknown";
  return Object.prototype.hasOwnProperty.call(AI_KEY_SOURCE_LABELS, key)
    ? AI_KEY_SOURCE_LABELS[key]
    : AI_KEY_SOURCE_LABELS.unknown;
}

// ─── Connections ─────────────────────────────────────────────────────────────

/**
 * One external connection from `GET /api/connections/health`, classified from its own
 * `configured` flag. The browser worker reports execution availability instead of a configured
 * switch, so it has its own arm and never borrows "configured".
 */
export function classifyConnection(kind, payload) {
  if (!isObject(payload)) return freezeResult("unknown", "connection_status_not_reported");

  if (kind === "browser_worker") {
    if (payload.execution_available === true) return freezeResult("enabled", "browser_execution_available");
    if (payload.execution_available === false) {
      return freezeResult("unavailable", asText(payload.blocked_reason) || "browser_execution_unavailable");
    }
    return freezeResult("unknown", "execution_availability_not_reported");
  }

  if (payload.configured === true) return freezeResult("enabled", "configured");
  if (payload.configured === false) {
    if (kind === "openclaw" && payload.adapter === "mock") {
      return freezeResult("unavailable", "mock_adapter_no_real_connection");
    }
    if (kind === "teamviewer" && payload.credential_source === "invalid_reference") {
      return freezeResult("unavailable", "invalid_secret_reference");
    }
    return freezeResult("requires_configuration", "configuration_missing");
  }
  return freezeResult("unknown", "configured_not_reported");
}

/**
 * A connector definition from `GET /api/agents/connector-types`. `implemented: false` is
 * `unavailable` (the product cannot offer it), never `disabled` (nobody switched it off).
 */
export function classifyConnectorDefinition(definition) {
  if (!isObject(definition) || !asText(definition.connector_id)) {
    return freezeResult("unknown", "connector_definition_unreadable");
  }
  if (definition.implemented === false) return freezeResult("unavailable", "connector_not_implemented");
  if (definition.implemented === true) return freezeResult("enabled", "connector_implemented");
  return freezeResult("unknown", "implemented_not_reported");
}

// ─── Tools ───────────────────────────────────────────────────────────────────

const TOOL_STATUS_STATES = Object.freeze({
  available: "enabled",
  disabled: "disabled",
  requires_connected_node: "unavailable",
  requires_configuration: "requires_configuration",
});

/**
 * A tool's operational state from the `tool_registry` block of `GET /api/connections/health`.
 * A registry entry with no status (the `GET /api/agents/tools` shape) is `unknown`; registration
 * is not evidence that a tool can run.
 */
export function classifyToolRecord(tool) {
  if (!isObject(tool) || !(asText(tool.id) || asText(tool.name))) {
    return freezeResult("unknown", "tool_record_unreadable");
  }
  const status = asText(tool.status, 80);
  if (!status) return freezeResult("unknown", "operational_status_not_reported");
  const state = TOOL_STATUS_STATES[status];
  return state ? freezeResult(state, status) : freezeResult("unknown", `unrecognised_status:${status}`);
}

/**
 * The tool's authorization dimension, kept separate from its operational state: a tool can be
 * unavailable and still require an approval if it ever becomes executable.
 */
export function toolAuthorization(tool) {
  if (!isObject(tool)) {
    return Object.freeze({ key: "unknown", label: "לא ידוע", approval: null, strong: null, audit: null });
  }
  const approval = asBool(tool.approval_required);
  const strong = asBool(tool.strong_approval_required);
  const audit = asBool(tool.audit_required);
  if (strong === true) return Object.freeze({ key: "strong_approval_required", label: "אישור כפול", approval, strong, audit });
  if (approval === true) return Object.freeze({ key: "approval_required", label: "נדרש אישור", approval, strong, audit });
  if (approval === false && strong === false) return Object.freeze({ key: "not_required", label: "לא נדרש", approval, strong, audit });
  return Object.freeze({ key: "unknown", label: "לא ידוע", approval, strong, audit });
}

// ─── Skills ──────────────────────────────────────────────────────────────────

/**
 * One external Skill candidate from `GET /api/tech-scout/discoveries`. A pending candidate is
 * `pending_review`; an adopted one is `enabled`; ignored/rejected ones are explicitly `disabled`;
 * watching/piloting candidates have been looked at but are not adopted, so they are `unavailable`
 * for production use rather than mislabelled as still-pending.
 */
export function classifySkillCandidate(candidate) {
  if (!isObject(candidate) || !asText(candidate.id)) {
    return freezeResult("unknown", "skill_candidate_unreadable");
  }
  switch (candidate.status) {
    case "pending":
      return freezeResult("pending_review", "awaiting_human_review");
    case "adopted":
      return freezeResult("enabled", "adopted");
    case "ignored":
    case "rejected":
      return freezeResult("disabled", "not_adopted");
    case "watching":
    case "piloting":
      return freezeResult("unavailable", "observed_or_piloting_not_adopted");
    default:
      return freezeResult("unknown", "candidate_status_not_reported");
  }
}

/**
 * An employee row from `GET /api/skills/leaderboard`. `agent_enabled` is the employee's own
 * switch; the outcome evidence is disclosed separately and is never turned into a state.
 */
export function classifyEmployeeEnablement(report) {
  if (!isObject(report) || !asText(report.agent_id)) {
    return freezeResult("unknown", "employee_report_unreadable");
  }
  if (report.agent_enabled === true) return freezeResult("enabled", "employee_enabled");
  if (report.agent_enabled === false) return freezeResult("disabled", "employee_disabled");
  return freezeResult("unknown", "agent_enabled_not_reported");
}

// ─── Health (a separate dimension from administration state) ─────────────────

export const PROVIDER_HEALTH_META = Object.freeze({
  healthy: Object.freeze({ key: "healthy", label: "בריא", cls: LIVE_STATUS_CLS.active }),
  degraded: Object.freeze({ key: "degraded", label: "פגום", cls: LIVE_STATUS_CLS.requires_configuration }),
  unreachable: Object.freeze({ key: "unreachable", label: "לא נגיש", cls: LIVE_STATUS_CLS.not_connected }),
  unknown: Object.freeze({ key: "unknown", label: "לא ידוע", cls: LIVE_STATUS_CLS.unknown }),
});

export function providerHealthMeta(health) {
  return typeof health === "string" && Object.prototype.hasOwnProperty.call(PROVIDER_HEALTH_META, health)
    ? PROVIDER_HEALTH_META[health]
    : PROVIDER_HEALTH_META.unknown;
}

// ─── Safe display helpers ────────────────────────────────────────────────────

/** Bounded display text. Objects, arrays and control characters never reach the DOM. */
export function adminDisplayText(value, max = 160) {
  if (typeof value !== "string") return null;
  let out = "";
  for (const ch of value) {
    const code = ch.codePointAt(0);
    const unsafe =
      code <= 0x1f ||
      (code >= 0x7f && code <= 0x9f) ||
      (code >= 0x200b && code <= 0x200f) ||
      (code >= 0x2028 && code <= 0x202e) ||
      (code >= 0x2060 && code <= 0x206f) ||
      code === 0xfeff;
    if (!unsafe) out += ch;
  }
  const trimmed = out.trim();
  return trimmed ? trimmed.slice(0, max) : null;
}

/** Integer or null — a score, count or latency is never invented from a missing number. */
export function adminInteger(value, { min = null, max = null } = {}) {
  if (typeof value !== "number" || !Number.isInteger(value)) return null;
  if (min != null && value < min) return null;
  if (max != null && value > max) return null;
  return value;
}
